// Format type constants 
export const INTEGER = "NUMBER_0";
export const DECIMAL_1 = "NUMBER_1";
export const DECIMAL_2 = "NUMBER_2";
export const DECIMAL_3 = "NUMBER_3";
export const DECIMAL_4 = "NUMBER_4";
// Get decimal and thousand separators based on current locale 
const locale = navigator.language || "en-US";
const parts = new Intl.NumberFormat(locale).formatToParts(1234567.89);
const decimalSymbol = parts.find(p => p.type === "decimal")?.value || ".";
const thousandSymbol = parts.find(p => p.type === "group")?.value || ",";
// Dynamic decimal and thousand symbols 
export const DECIMAL_POINTER: string = decimalSymbol;
export const THOUSAND_DIGIT_SYMBOL: string = thousandSymbol;
/**
 * Format the number string to request format string 
 * @param numberStr - The source value 
 * @param decimalPlaces - The number of decimal digits 
 * @param autoPadEnd - Whether to pad decimal part with zeros if insufficient digits 
 * @param showDecimalPointerAlways - Whether to always show decimal separator 
 * @returns Formatted string 
 */
export const formatNumberStr = (
  numberStr: string,
  decimalPlaces: number,
  autoPadEnd: boolean = true,
  showDecimalPointerAlways: boolean = false
): string => {
  if (numberStr === null || numberStr === '') return '';
  const regex = new RegExp(`\\${THOUSAND_DIGIT_SYMBOL}`, 'g');
  numberStr = numberStr.replace(regex, '');
  const hasDecimalPointer = numberStr.includes(DECIMAL_POINTER);
  const parts = numberStr.split(DECIMAL_POINTER);
  const integerPart = parts[0] || '0';
  let decimalPart = parts[1] && parts[1].length >= decimalPlaces
    ? parts[1].substring(0, decimalPlaces)
    : (parts[1] ? parts[1] : '');
  decimalPart = decimalPart
    ? (autoPadEnd ? decimalPart.padEnd(decimalPlaces, '0') : decimalPart)
    : (autoPadEnd ? ''.padEnd(decimalPlaces, '0') : '');
  const integerPartWithSeparator = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    THOUSAND_DIGIT_SYMBOL
  );
  return `${integerPartWithSeparator}${decimalPart ? `${DECIMAL_POINTER}${decimalPart}` : (showDecimalPointerAlways && hasDecimalPointer ? DECIMAL_POINTER : '')}`;
};
/**
 * Format the number for input component 
 * @param num - The number to format 
 * @param decimalPlaces - Number of decimal places 
 * @returns Formatted string 
 */
export const formatNumber = (num: number, decimalPlaces: number): string => {
  if (num === null) return '';
  let rounded = num;
  switch (decimalPlaces) {
    case 4:
      rounded = Math.round(num * 10000) / 10000;
      break;
    case 3:
      rounded = Math.round(num * 1000) / 1000;
      break;
    case 2:
      rounded = Math.round(num * 100) / 100;
      break;
    case 1:
      rounded = Math.round(num * 10) / 10;
      break;
    default:
      rounded = Math.round(num);
  }
  const [integerPart, decimalPart] = rounded.toString().split('.') || ['0', ''];
  const padDecimal = decimalPart ? decimalPart.padEnd(decimalPlaces, '0') : ''.padEnd(decimalPlaces, '0');
  const integerWithSeparator = integerPart?.replace(/\B(?=(\d{3})+(?!\d))/g, THOUSAND_DIGIT_SYMBOL);
  return `${integerWithSeparator}${padDecimal ? `${DECIMAL_POINTER}${padDecimal}` : ''}`;
};
/**
 * Format number for table with format string 
 * @param num - The number to format 
 * @param decimalString - Format string (e.g., "NUMBER_2")
 * @returns Formatted string 
 */
/**
 * Format number for table with format string
 * 
 * @param num - The number to format
 * @param decimalString - Format string (e.g., "NUMBER_2")
 * @returns Formatted number string
 */
export const formatNumberWithFormatString = (
  num: number,
  decimalString: string
): string => {
  if (num === null || !decimalString || !decimalString.includes("_")) return '';

  const decimalDigits = decimalString.split("_")[1];
  const decimalPlaces = parseInt(decimalDigits, 10);

  const [integerPart, decimalPart = ''] = num.toString().split('.');
  const paddedDecimal = decimalPart.padEnd(decimalPlaces, '0');

  const integerWithSeparator = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    THOUSAND_DIGIT_SYMBOL
  );

  return `${integerWithSeparator}${paddedDecimal ? `${DECIMAL_POINTER}${paddedDecimal}` : ''
    }`;
};
/**
 * Convert formatted number string to float 
 * @param numberStr - Formatted number string 
 * @returns Parsed float number 
 */
export const formatStrToNumber = (numberStr: string): number => {
  let cleanedStr = numberStr.replace(new RegExp(`\\${THOUSAND_DIGIT_SYMBOL}`, 'g'), '');
  let formattedStr = cleanedStr.replace(new RegExp(`\\${DECIMAL_POINTER}`), '.');
  return parseFloat(formattedStr);
};
/**
 * Validate number string format 
 * @param numberStr - Number string to validate 
 * @param decimalPlaces - Allowed decimal places 
 * @param isAllowNegative - Whether negative values are allowed 
 * @returns Validation result 
 */
export const validateNumberStr = (numberStr: string, decimalPlaces: number, isAllowNegative: boolean = false): boolean => {
  const sign = isAllowNegative ? "-?" : "";
  const regex = new RegExp(`^${sign}[\\d]*[${DECIMAL_POINTER}]?[\\d]{0,${decimalPlaces}}$`);
  return regex.test(numberStr);
};
/**
 * Validate number string with length limit 
 * @param maxLength - Maximum allowed length 
 * @param numberStr - Number string to validate 
 * @param decimalPlaces - Allowed decimal places 
 * @param isAllowNegative - Whether negative values are allowed 
 * @returns Validation result 
 */
export const validateNumberStrWithLength = (
  maxLength: number,
  numberStr: string,
  decimalPlaces: number,
  isAllowNegative: boolean = false
): boolean => {
  const integerLength = maxLength - (decimalPlaces ? decimalPlaces + 1 : 0);
  const sign = isAllowNegative ? "-?" : "";
  const regex = new RegExp(`^${sign}(\\d{0,${integerLength}})(\\${DECIMAL_POINTER}\\d{0,${decimalPlaces}})?$`);
  return regex.test(numberStr);
};
/**
 * Validate percentage string 
 * @param numberStr - Percentage string to validate 
 * @param decimalPlaces - Allowed decimal places 
 * @param isAllowNegative - Whether negative percentages are allowed 
 * @returns Validation result 
 */
export const validatePercentageStr = (
  numberStr: string,
  decimalPlaces: number,
  isAllowNegative?: boolean
): boolean => {
  // Escape dynamic decimal pointer (avoid regex special character conflicts)
  const escapedDecimalPointer = DECIMAL_POINTER.replace(/[.*+?^${}() |[\]\\]/g, '\\$&');
  // Build regex pattern dynamically 
  let regexPattern: string;
  if (isAllowNegative) {
    regexPattern = `^\\-?[\\d]{0,3}(?:${escapedDecimalPointer}[\\d]{0,${decimalPlaces}})?%$`;
  } else {
    regexPattern = `^[\\d]{0,3}(?:${escapedDecimalPointer}[\\d]{0,${decimalPlaces}})?%$`;
  }
  return new RegExp(regexPattern).test(numberStr);
};
/**
 * Convert string to editable number (remove thousand separators)
 * @param numberStr - Formatted number string 
 * @returns Cleaned number string 
 */
export const formatStrForEditable = (numberStr: string): string => {
  return numberStr.replace(new RegExp(`\\${THOUSAND_DIGIT_SYMBOL}`, 'g'), '');
};
/**
 * Convert number string to float (alternative implementation)
 * @param numberStr - Formatted number string 
 * @returns Parsed float number 
 */
export const formatStrToNumberForCom = (numberStr: string): number => {
  let cleanedStr = numberStr.replace(new RegExp(`\\${THOUSAND_DIGIT_SYMBOL}`, 'g'), '');
  let formattedStr = cleanedStr.replace(new RegExp(`\\${DECIMAL_POINTER}`), '.');
  return parseFloat(formattedStr);
};